/**
 * Главная страница с QR-кодом
 */
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useWorkerStore } from '../store/workerStore'
import { QRDisplay } from '../components/QRDisplay'
import { QRGenerator } from '../services/qrGenerator'
import { DebugLogger } from '../components/DebugLogger'
import { BottomNav } from '../components/BottomNav'
import './QRPage.css'

type NavTab = 'qr' | 'sites' | 'schedule' | 'profile'

export function QRPage() {
  const navigate = useNavigate()
  const { employeeId, fullName, role, isOnboarded, reset } = useWorkerStore()
  const [showResetDialog, setShowResetDialog] = useState(false)
  const [activeTab, setActiveTab] = useState<NavTab>('qr')
  
  useEffect(() => {
    // Если не прошёл онбординг, перенаправляем на страницу онбординга
    if (!isOnboarded) {
      navigate('/onboarding')
      return
    }
  }, [isOnboarded, navigate])
  
  // Обработка изменения таба
  const handleTabChange = (tab: NavTab) => {
    if (tab === 'profile') {
      navigate('/profile')
    } else if (tab === 'sites') {
      navigate('/sites')
    } else if (tab === 'schedule') {
      navigate('/schedule')
    } else {
      setActiveTab(tab)
    }
  }
  
  const handleReset = () => {
    // Очищаем кеш QR перед сбросом
    QRGenerator.clearCache()
    reset()
    navigate('/onboarding')
  }
  
  if (!isOnboarded) {
    return null
  }
  
  return (
    <div className="qr-page">
      <div className="qr-page__header">
        <h1 className="qr-page__title">Мой пропуск</h1>
        <p className="qr-page__subtitle">Отсканируйте QR-код для входа</p>
        {fullName && (
          <div className="qr-page__user">
            <div className="qr-page__name">{fullName}</div>
            {employeeId && (
              <div className="qr-page__id">{employeeId}</div>
            )}
          </div>
        )}
      </div>
      
      <div className="qr-page__content">
        <QRDisplay />
        
        {/* Роль администратора отображается как бейдж */}
        {role === 'admin' && (
          <div className="qr-page__admin-badge">
            <span>👑 Администратор</span>
          </div>
        )}
      </div>
      
      <div className="qr-page__footer">
        <button
          className="qr-page__settings"
          onClick={() => setShowResetDialog(true)}
          style={{
            padding: '0.75rem 1.5rem',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            color: 'white',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '0.95rem',
            marginRight: '0.5rem'
          }}
        >
          ⚙️ Сбросить приложение
        </button>
        <button
          className="qr-page__logout"
          onClick={handleReset}
        >
          Выйти
        </button>
      </div>
      
      {/* Reset confirmation dialog */}
      {showResetDialog && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '1rem'
          }}
          onClick={() => setShowResetDialog(false)}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '16px',
              padding: '2rem',
              maxWidth: '400px',
              width: '100%',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 1rem 0', color: '#333', fontSize: '1.5rem' }}>
              Сбросить приложение?
            </h3>
            <p style={{ margin: '0 0 1.5rem 0', color: '#666', lineHeight: '1.6' }}>
              Все данные (ключи, профиль) будут удалены. Вам нужно будет заново отсканировать QR-код регистрации от администратора.
            </p>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button
                onClick={() => setShowResetDialog(false)}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  backgroundColor: '#f0f0f0',
                  color: '#333',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: 500
                }}
              >
                Отмена
              </button>
              <button
                onClick={() => {
                  setShowResetDialog(false)
                  handleReset()
                }}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  backgroundColor: '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: 500
                }}
              >
                Сбросить
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Debug logger для отладки на мобильных устройствах */}
      <DebugLogger />
      
      {/* Нижняя навигация */}
      <BottomNav activeTab={activeTab} onTabChange={handleTabChange} />
    </div>
  )
}

